import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { haptic, webApp } from '../lib/telegram';

const TABS = [
  { to: '/feed', label: 'Отзывы', icon: FeedIcon },
  { to: '/analytics', label: 'Аналитика', icon: ChartIcon },
  { to: '/checklist', label: 'Чек-лист', icon: CheckIcon },
  { to: '/settings', label: 'Ещё', icon: MoreIcon },
] as const;

const ROOT_ROUTES = new Set<string>(TABS.map((tab) => tab.to));

/**
 * Оболочка приложения.
 *
 * Панель навигации — полупрозрачный материал, под который уходит контент:
 * так лента не обрывается жёсткой линией, а видно, что список продолжается.
 * Системная кнопка «назад» Telegram включается на всех вложенных экранах,
 * чтобы выход был там же, где его ожидают в любом другом мини-аппе.
 */
export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const isRoot = ROOT_ROUTES.has(location.pathname);

  useEffect(() => {
    const app = webApp();
    if (!app) return;

    const goBack = () => {
      haptic.tap();
      navigate(-1);
    };

    if (isRoot) {
      app.BackButton.hide();
      return;
    }
    app.BackButton.show();
    app.BackButton.onClick(goBack);
    return () => {
      app.BackButton.offClick(goBack);
      app.BackButton.hide();
    };
  }, [isRoot, navigate]);

  return (
    <div className="flex min-h-full flex-col bg-[var(--color-bg)]">
      <main key={location.pathname} className="surface-in safe-top flex-1 pb-[var(--tab-bar-height)]">
        <Outlet />
      </main>

      <nav
        className="material safe-bottom fixed inset-x-0 bottom-0 z-20 border-t border-[var(--color-separator)]"
        style={{
          background: 'color-mix(in srgb, var(--color-bg) 78%, transparent)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        }}
      >
        <ul className="mx-auto flex max-w-lg">
          {TABS.map((tab) => (
            <li key={tab.to} className="flex-1">
              <NavLink
                to={tab.to}
                onClick={() => haptic.select()}
                className="pressable flex flex-col items-center gap-[3px] pt-2 pb-1"
              >
                {({ isActive }) => (
                  <>
                    <tab.icon active={isActive} />
                    <span
                      className="t-caption font-medium"
                      style={{ color: isActive ? 'var(--color-accent)' : 'var(--color-hint)' }}
                    >
                      {tab.label}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/*
  Иконки нарисованы вручную, а не взяты эмодзи: эмодзи рендерятся по-разному
  на платформах, съезжают по базовой линии и не подхватывают цвет активного таба.
*/

interface IconProps {
  active: boolean;
}

const iconColor = (active: boolean): string => (active ? 'var(--color-accent)' : 'var(--color-hint)');

function FeedIcon({ active }: IconProps) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.4-.7L3 21l1.9-5.1A8.3 8.3 0 0 1 4 11.5 8.4 8.4 0 0 1 12.5 3 8.4 8.4 0 0 1 21 11.5Z"
        stroke={iconColor(active)}
        strokeWidth={active ? 2.1 : 1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={active ? 'color-mix(in srgb, var(--color-accent) 16%, transparent)' : 'none'}
      />
    </svg>
  );
}

function ChartIcon({ active }: IconProps) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 19V10M9.3 19V5M14.7 19v-6M20 19v-9"
        stroke={iconColor(active)}
        strokeWidth={active ? 2.4 : 1.9}
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon({ active }: IconProps) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="8.6"
        stroke={iconColor(active)}
        strokeWidth={active ? 2.1 : 1.7}
        fill={active ? 'color-mix(in srgb, var(--color-accent) 16%, transparent)' : 'none'}
      />
      <path
        d="m8.4 12.2 2.5 2.5 4.7-4.9"
        stroke={iconColor(active)}
        strokeWidth={active ? 2.3 : 1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MoreIcon({ active }: IconProps) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {[6, 12, 18].map((cx) => (
        <circle key={cx} cx={cx} cy="12" r={active ? 2.1 : 1.8} fill={iconColor(active)} />
      ))}
    </svg>
  );
}
